import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@starling/core/api/errors';
import { ListingForm } from './ListingForm';
import { useMyListingQuery, useUpdateListingMutation, type Listing } from './hooks';
import styles from './PostPage.module.css';

// Правка своего объявления (V1.3-e2) — форма как /post (общий ListingForm),
// но только поля ListingUpdate: организация, лента и статус через PATCH не
// меняются (VD9). Archived править МОЖНО — «правка перед новой публикацией»
// (решение V1.1-b №8); removed → 404 на бэке, из списка сюда не ведём, а при
// прямом заходе показываем «снято модерацией — правка недоступна».
//
// Источник данных: объект списка через router-state (обычный путь — ноль
// запросов); прямой заход по URL / перезагрузка — авторская карточка
// `GET /vision/my/listings/{id}` (V1.3-e3, VD12): любой статус вкл. removed,
// нет / чужой организации → 404 `listing_not_found`.

export function EditListingPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state as { listing?: Listing; returnTo?: string } | null;
  const stateListing = state?.listing;
  // Откуда пришли: с карточки (`returnTo` = /listings/:id) — туда и вернёмся
  // после сохранения; из «Моих объявлений» — в список (как раньше).
  const returnTo = state?.returnTo ?? '/my/listings';
  const query = useMyListingQuery(stateListing ? undefined : id);
  const listing = stateListing ?? query.data ?? null;

  const mutation = useUpdateListingMutation(id ?? '');
  const [error, setError] = useState<string | null>(null);

  let body: JSX.Element;
  if (listing && listing.status !== 'removed') {
    body = (
      <div className={styles.card}>
        <ListingForm
          initial={{
            feed: listing.feed,
            categoryId: listing.category_id,
            title: listing.title,
            description: listing.description,
            city: listing.city,
            district: listing.district ?? '',
            phone: listing.contact_phone ?? '',
            whatsapp: listing.contact_whatsapp,
            telegram: listing.contact_telegram ?? '',
          }}
          submitLabel={t('myListings.edit.submit')}
          pending={mutation.isPending}
          error={error}
          onSubmit={(values) => {
            setError(null);
            mutation.mutate(
              {
                category_id: values.categoryId,
                title: values.title,
                description: values.description,
                city: values.city,
                // VD9: очистить nullable district нельзя — пустое поле просто
                // не отправляем (прежнее значение останется).
                ...(values.district ? { district: values.district } : {}),
                // Контакты (№16) — только изменённые; пустая строка = очистить
                // (бэкенд следит, чтобы остался хотя бы один — 422 contact_required).
                ...(values.phone !== (listing.contact_phone ?? '')
                  ? { contact_phone: values.phone }
                  : {}),
                // №22: флаг WhatsApp — тоже только при изменении (в т.ч. снятие
                // вместе с очисткой телефона).
                ...(values.whatsapp !== listing.contact_whatsapp
                  ? { contact_whatsapp: values.whatsapp }
                  : {}),
                ...(values.telegram !== (listing.contact_telegram ?? '')
                  ? { contact_telegram: values.telegram }
                  : {}),
              },
              {
                onSuccess: () => navigate(returnTo),
                onError: (err) => {
                  if (err instanceof ApiError) {
                    if (err.code === 'invalid_phone')
                      return setError(t('post.errors.invalidPhone'));
                    if (err.code === 'invalid_telegram')
                      return setError(t('post.errors.invalidTelegram'));
                    if (err.code === 'contact_required')
                      return setError(t('post.errors.contactRequired'));
                    if (err.code === 'whatsapp_requires_phone')
                      return setError(t('post.errors.whatsappRequiresPhone'));
                    if (err.code === 'category_not_found')
                      return setError(t('post.errors.category'));
                    // 404 — объявление успело стать removed/исчезнуть;
                    // 403 — организация выключена/расподтверждена.
                    if (err.status === 404) return setError(t('myListings.edit.errors.gone'));
                    if (err.status === 403) return setError(t('post.errors.orgGate'));
                  }
                  setError(t('myListings.edit.errors.generic'));
                },
              },
            );
          }}
        />
      </div>
    );
  } else if (
    listing ||
    (query.isError && query.error instanceof ApiError && query.error.status === 404)
  ) {
    // listing здесь может быть только removed (снято модерацией — правка
    // закрыта, VD6); 404 — нет среди своих / чужой организации.
    body = (
      <div className={styles.state}>
        {listing ? t('myListings.edit.removed') : t('myListings.edit.notFound')}
        <div>
          <Link to="/my/listings" className={styles.retry}>
            {t('myListings.edit.toList')}
          </Link>
        </div>
      </div>
    );
  } else if (query.isError) {
    body = (
      <div className={`${styles.state} ${styles.stateError}`}>
        {t('myListings.error')}
        <div>
          <button type="button" className={styles.retry} onClick={() => void query.refetch()}>
            {t('myListings.retry')}
          </button>
        </div>
      </div>
    );
  } else {
    body = <div className={styles.state}>{t('myListings.edit.loading')}</div>;
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.titleRow}>
        <h1 className={styles.pageTitle}>{t('myListings.edit.title')}</h1>
        <button
          type="button"
          className={styles.close}
          aria-label={t('post.close')}
          onClick={() => navigate(-1)}
        >
          ×
        </button>
      </div>
      {body}
    </div>
  );
}
